import type { DeploymentRecord } from '@/services/deploymentService';
import { useDeployClock } from '@/hooks/useDeployClock';
import type { ProgressSnapshot } from '@/utils/deployProgressPoller';
import { MAX_POLLED_RUNS } from '@/utils/deployProgressPoller';
import {
  PHASE_HINTS,
  PHASE_LABELS,
  progressPercent,
  resolvePhase,
  type DeployPhase,
  type DeployProgress,
  type LogPhase,
} from '@/utils/deployProgress';
import { ACCESS_WARNING_AFTER_MS, attemptStartedAt, isStuck, secondsToRetry } from '@/utils/deployStatus';
import { formatDuration, formatSeconds } from '@/utils/relativeTime';

export const SLOT_HINT = `The portal reads live progress for ${MAX_POLLED_RUNS} runs at a time, to stay inside the CloudWatch request limit. This run gets a slot when another finishes.`;
export const STUCK_HINT =
  'Running for over two hours. A deploy stops itself at two hours, so this run is probably dead. Use More, then Mark failed.';
export const ACCESS_NOTE = 'Past 60 minutes this run loses its AWS access and fails.';

/** "Second attempt starts in 45s", counting down. */
export function retryLine(seconds: number): string {
  return seconds > 0 ? `Second attempt starts in ${formatSeconds(seconds)}` : 'Second attempt is starting';
}

/** Everything a live run's status line shows, worked out once for the card and for the table row. */
export interface RunProgressView {
  /** The word for where the run is: a phase, or Running, Stuck, Retrying. */
  label: string;
  /** What that word means, for the tooltip. */
  hint: string;
  phase: DeployPhase;
  /** The run's progress, when it is being read and has come back. */
  progress?: DeployProgress;
  /** 0 to 100, or null when there is nothing to measure yet. */
  percent: number | null;
  /** The plan line is in: the resource counts are known. */
  hasCount: boolean;
  showBar: boolean;
  /** Running for over two hours: dead. */
  stuck: boolean;
  /** Past the cap on runs read at once: no progress until a slot frees. */
  waiting: boolean;
  /** The first read has not come back yet. */
  reading: boolean;
  /** Seconds until the second attempt starts. Null when the run is not between two attempts. */
  retryIn: number | null;
  /** "apply 9m 05s": how long the run has been in this phase. Null when that is not known. */
  phaseTime: string | null;
  /** "51m 37s": how long the whole run has taken. */
  total: string;
  /** The attempt is close to the hour its AWS access lasts. */
  nearAccessLimit: boolean;
}

/** A live run's status, ticking each second. One source for the card, the table row and the log page. */
export function useRunProgressView(record: DeploymentRecord, snapshot?: ProgressSnapshot): RunProgressView {
  const now = useDeployClock();
  const hasTask = Boolean(record.ecsTaskArn);
  const stuck = isStuck(record, now);
  const retryIn = stuck ? null : secondsToRetry(record, now);
  const waiting = hasTask && Boolean(snapshot?.waiting) && retryIn === null;
  // A stuck run is dead and a waiting one is not being read: what was last seen of either is not live progress.
  const progress = snapshot?.loaded && !waiting && !stuck && retryIn === null ? snapshot.progress : undefined;
  // The first read is in flight: the run is somewhere past "Starting", and saying which would be a guess.
  const reading = hasTask && !waiting && !stuck && retryIn === null && !progress;

  const phase = resolvePhase(progress, { hasTask });
  let label = PHASE_LABELS[phase];
  let hint = PHASE_HINTS[phase];
  if (stuck) {
    label = 'Stuck';
    hint = STUCK_HINT;
  } else if (retryIn !== null) {
    label = 'Retrying';
    hint = 'The first attempt failed. The pipeline is about to run the deploy a second time.';
  } else if (waiting) {
    label = 'Running';
    hint = SLOT_HINT;
  } else if (reading) {
    label = 'Running';
    hint = 'Reading the progress of this run.';
  }

  // Once the apply is complete there is no phase left to time: the line says "Finishing up".
  const phaseStartedAt =
    progress && phase !== 'queued' && phase !== 'done' ? progress.phaseAt[phase as LogPhase] : undefined;
  const phaseTime = phaseStartedAt
    ? `${label.toLowerCase()} ${formatSeconds(Math.max(0, (now - phaseStartedAt) / 1000))}`
    : null;
  const attemptStart = attemptStartedAt(record);

  return {
    label,
    hint,
    phase,
    progress,
    percent: stuck || waiting || retryIn !== null ? null : progressPercent(progress),
    hasCount: Boolean(progress && progress.total > 0),
    showBar: !stuck && !waiting && retryIn === null,
    stuck,
    waiting,
    reading,
    retryIn,
    phaseTime,
    total: formatDuration(record.startedAt, undefined, now),
    nearAccessLimit:
      !stuck &&
      retryIn === null &&
      phase !== 'done' &&
      attemptStart !== null &&
      now - attemptStart > ACCESS_WARNING_AFTER_MS,
  };
}
